import React, { useEffect } from "react";
import {
  BedDouble,
  MapPin,
  Navigation,
  Phone,
  Mail,
  Wifi,
  Car,
  Waves,
  TreePine,
  CalendarCheck,
  Star,
} from "lucide-react";
import styles from "./GoogleAdsLanding.module.css";

const BOOK_URL =
  "https://book-directonline.com/properties/southtweedmidirect";
const PHONE = "02 6672 3248";
const PHONE_HREF = "tel:0266723248";
const EMAIL = "arabellamotorinn@gmail.com";
const ADDRESS = "9-11 Minjungbal Drive, Tweed Heads South, NSW, 2486";
const MAPS_URL =
  "https://www.google.com/maps/place/Arabella+Motor+Inn/@-28.1880296,153.5379114,17z/data=!4m9!3m8!1s0x6b90ffc81fcd0c6d:0xba0e6aef62dcb581!5m2!4m1!1i2!8m2!3d-28.1880344!4d153.5404863!16s%2Fg%2F1tm88lxb?hl=en&entry=ttu";
const GOOGLE_REVIEWS_URL =
  "https://www.google.com/travel/hotels/entity/CgsIgevylvbdmoe6ARAB/reviews?q=arabella%20motor%20inn";

const rooms = [
  {
    name: "Queen Room",
    image: "/images/gallery/thumbs/queen-img2.webp",
    description:
      "Comfortable queen room with air conditioning/heating, microwave, toaster, bar fridge, tea & coffee facilities and free Wi-Fi.",
    amenities: ["Free Wi-Fi", "Parking", "Air conditioning", "Kitchenette"],
  },
  {
    name: "Deluxe Room",
    image: "/images/gallery/thumbs/queen-img8.webp",
    description:
      "A well-appointed deluxe room with air conditioning/heating, microwave, toaster, bar fridge, tea & coffee facilities and daily room service.",
    amenities: ["Free Wi-Fi", "Parking", "Air conditioning", "Daily service"],
  },
  {
    name: "Deluxe Twin",
    image: "/images/gallery/thumbs/twin-img3.webp",
    description:
      "One queen bed and one single bed. Includes air conditioning/heating, microwave, toaster, bar fridge, tea & coffee facilities and free Wi-Fi.",
    amenities: ["Queen + single", "Free Wi-Fi", "Parking", "Kitchenette"],
  },
  {
    name: "Family Room",
    image: "/images/gallery/thumbs/family-img5.webp",
    description:
      "Family-friendly room with flexible bedding options. Air conditioning/heating, microwave, toaster, bar fridge, tea & coffee facilities and free Wi-Fi.",
    amenities: ["Family-friendly", "Free Wi-Fi", "Parking", "Kitchenette"],
  },
];

const benefits = [
  {
    icon: BedDouble,
    title: "Comfortable Rooms",
    text: "Clean, well-maintained motel rooms for couples, families and travellers.",
  },
  {
    icon: MapPin,
    title: "Convenient Location",
    text: "Stay in Tweed Heads South with easy access around the Tweed region.",
  },
  {
    icon: Navigation,
    title: "Easy Access to Murwillumbah",
    text: "A practical base when visiting Murwillumbah and the Northern Rivers.",
  },
  {
    icon: Waves,
    title: "Close to Tweed Heads",
    text: "Conveniently placed for Tweed Heads, nearby beaches and day trips.",
  },
  {
    icon: CalendarCheck,
    title: "Easy Direct Booking",
    text: "Check availability and book your stay online in a few steps.",
  },
];

const whyStay = [
  {
    icon: Waves,
    title: "Saltwater Pool",
    text: "Refresh after a day exploring the Tweed Valley and coast.",
  },
  {
    icon: Car,
    title: "Free On-Site Parking",
    text: "Park on site with easy access to your room.",
  },
  {
    icon: Wifi,
    title: "Free High-Speed Wi-Fi",
    text: "Stay connected for work or leisure throughout your visit.",
  },
  {
    icon: TreePine,
    title: "Peaceful Courtyard",
    text: "Unwind outdoors in a quiet courtyard setting.",
  },
];

const reviews = [
  {
    name: "Clinton",
    source: "Google Review",
    text: "Great service, lovely rooms, highly recommend. Rooms 5.0, Service 5.0, Location 5.0.",
  },
  {
    name: "Emma Wilson",
    source: "Guest Review",
    text: "Stayed for a short trip and everything was smooth from check-in to checkout. Clean room and very friendly staff. 5/5.",
  },
  {
    name: "Jason Thomas",
    source: "Guest Review",
    text: "Comfortable bed, tidy bathroom, and a peaceful night. Great value for the location and service quality. 5/5.",
  },
  {
    name: "Geoff Coy",
    source: "Guest Review",
    text: "Very convenient location and welcoming team. The room was clean and comfortable throughout our stay. 5/5.",
  },
];

function BookButton({ className, label = "BOOK YOUR STAY", conversion = "booking-click" }) {
  return (
    <a
      href={BOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      data-conversion={conversion}
    >
      {label}
    </a>
  );
}

function CallButton({ className, label = "CALL NOW" }) {
  return (
    <a
      href={PHONE_HREF}
      className={className}
      data-conversion="phone-click"
      aria-label={`Call ${PHONE}`}
    >
      {label}
    </a>
  );
}

const GoogleAdsLanding = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title =
      "Arabella Motor Inn | Accommodation Near Murwillumbah & Tweed Heads";

    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") || "";
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Book your stay at Arabella Motor Inn for comfortable accommodation near Murwillumbah and Tweed Heads. Check availability and book your stay today."
    );

    return () => {
      document.title = previousTitle;
      if (meta) meta.setAttribute("content", previousDescription);
    };
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <a href="#top" className={styles.brand}>
            <span className={styles.brandName}>ARABELLA</span>
            <span className={styles.brandSub}>MOTOR INN</span>
          </a>

          <nav className={styles.nav} aria-label="Page sections">
            <a href="#stay">Stay</a>
            <a href="#rooms">Rooms</a>
            <a href="#location">Location</a>
            <a href="#reviews">Reviews</a>
            <a href="#book">Book</a>
          </nav>

          <div className={styles.headerActions}>
            <CallButton className={styles.headerCall} label="CALL NOW" />
            <BookButton className={styles.headerBook} />
            <BookButton className={styles.headerBookMobile} label="BOOK" />
          </div>
        </div>
      </header>

      <main id="top">
        <section className={styles.hero}>
          <img
            className={styles.heroImage}
            src="/images/Arb. 1.jpg"
            alt="Arabella Motor Inn saltwater pool"
          />
          <div className={styles.heroOverlay} />
          <div className={styles.heroContent}>
            <p className={styles.heroBrand}>ARABELLA MOTOR INN</p>
            <h1>
              Comfortable Accommodation Near Murwillumbah &amp; Tweed Heads
            </h1>
            <p className={styles.heroCopy}>
              Enjoy a comfortable and convenient stay at Arabella Motor Inn,
              with easy access to Murwillumbah, Tweed Heads and the surrounding
              Northern Rivers.
            </p>
            <div className={styles.heroCtas}>
              <BookButton className={styles.primaryBtn} />
              <CallButton className={styles.secondaryBtn} />
            </div>
          </div>
        </section>

        <section id="stay" className={styles.benefits}>
          <div className={styles.container}>
            <h2>A Comfortable Stay in a Convenient Location</h2>
            <div className={styles.benefitGrid}>
              {benefits.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className={styles.benefitCard}>
                    <div className={styles.benefitIcon}>
                      <Icon size={22} />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="rooms" className={styles.rooms}>
          <div className={styles.container}>
            <h2>Choose Your Stay</h2>
            <p className={styles.sectionLead}>
              Select a comfortable room and book your stay directly with
              Arabella Motor Inn.
            </p>
            <div className={styles.roomGrid}>
              {rooms.map((room) => (
                <article key={room.name} className={styles.roomCard}>
                  <div className={styles.roomImageWrap}>
                    <img src={room.image} alt={room.name} loading="lazy" />
                  </div>
                  <div className={styles.roomBody}>
                    <h3>{room.name}</h3>
                    <p>{room.description}</p>
                    <ul className={styles.amenityList}>
                      {room.amenities.map((amenity) => (
                        <li key={amenity}>{amenity}</li>
                      ))}
                    </ul>
                    <BookButton
                      className={styles.roomBook}
                      conversion="room-booking-click"
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.why}>
          <div className={styles.container}>
            <h2>Everything You Need for a Comfortable Stay</h2>
            <div className={styles.whyGrid}>
              {whyStay.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className={styles.whyCard}>
                    <Icon size={24} />
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="location" className={styles.location}>
          <div className={styles.container}>
            <div className={styles.locationGrid}>
              <div>
                <h2>Stay Near Murwillumbah &amp; Tweed Heads</h2>
                <p>
                  Arabella Motor Inn is located in Tweed Heads South, making it
                  a convenient choice for travellers visiting Murwillumbah,
                  Tweed Heads and the wider Northern Rivers region.
                </p>
                <p>
                  Whether you are passing through, visiting family, or exploring
                  nearby coastal towns, our motel offers a comfortable base with
                  easy road access around the area.
                </p>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.mapLink}
                >
                  <MapPin size={18} /> View on Google Maps
                </a>
              </div>
              <div className={styles.locationVisual}>
                <img
                  src="/images/img2.jpg"
                  alt="Arabella Motor Inn courtyard and parking"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="reviews" className={styles.reviews}>
          <div className={styles.container}>
            <h2>What Our Guests Say</h2>
            <div className={styles.reviewGrid}>
              {reviews.map((review) => (
                <article key={review.name} className={styles.reviewCard}>
                  <div className={styles.stars} aria-label="5 star review">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <p>“{review.text}”</p>
                  <div className={styles.reviewMeta}>
                    <strong>{review.name}</strong>
                    <span>{review.source}</span>
                  </div>
                </article>
              ))}
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.reviewsLink}
            >
              View more Google reviews
            </a>
          </div>
        </section>

        <section id="book" className={styles.finalCta}>
          <img
            className={styles.finalImage}
            src="/images/ARB. 5.jpg"
            alt="Arabella Motor Inn entrance"
          />
          <div className={styles.finalOverlay} />
          <div className={styles.finalContent}>
            <h2>Ready to Book Your Stay?</h2>
            <p>
              Check availability and book your stay at Arabella Motor Inn today.
            </p>
            <div className={styles.heroCtas}>
              <BookButton className={styles.primaryBtn} />
              <CallButton className={styles.secondaryBtn} />
            </div>
          </div>
        </section>
      </main>

      <footer id="contact" className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.footerGrid}>
            <div>
              <h2>Arabella Motor Inn</h2>
              <p className={styles.footerAddress}>
                <MapPin size={18} />
                {ADDRESS}
              </p>
              <p>
                <Phone size={18} />
                <a href={PHONE_HREF} data-conversion="phone-click">
                  {PHONE}
                </a>
              </p>
              <p>
                <Mail size={18} />
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </p>
            </div>
            <div className={styles.footerActions}>
              <BookButton className={styles.primaryBtn} />
              <CallButton className={styles.secondaryBtn} />
            </div>
          </div>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} Arabella Motor Inn. All rights
            reserved.
          </p>
        </div>
      </footer>

      <div className={styles.mobileBar}>
        <BookButton className={styles.mobileBook} />
        <CallButton className={styles.mobileCall} />
      </div>
    </div>
  );
};

export default GoogleAdsLanding;
